import React, { useState } from "react";
import {
  applyBrandLogo,
  LOGO_POSITIONS,
  type ApplyBrandLogoResponse,
  type LogoPosition,
  type LogoVariant,
} from "../../../../services/contentService";
import { debug } from "../../../../utils/debugLogger";

/**
 * Put the brand mark on a generated image (SGL-344).
 *
 * The backend measures the pixels the mark will actually cover and suggests the
 * variant that contrasts with them. This component's job is to make that a
 * *suggestion*: it shows what was measured, and the flip is one click away and
 * never hidden behind a menu. The customer asked for exactly that — "You can
 * suggest, but I would want to be able to overwrite it. There's a gradient
 * behind it, so the decision COULD be iffy."
 *
 * Position matters as much as the variant. The same image can be light in one
 * corner and dark in another, so moving the mark re-measures rather than reusing
 * the previous answer — unless the customer has already overridden, in which case
 * their choice is carried across the move.
 */

const POSITION_LABELS: Record<LogoPosition, string> = {
  "top-left": "Top left",
  "top-center": "Top centre",
  "top-right": "Top right",
  "bottom-left": "Bottom left",
  "bottom-center": "Bottom centre",
  "bottom-right": "Bottom right",
};

const VARIANT_LABELS: Record<LogoVariant, string> = {
  light: "For light backgrounds",
  dark: "For dark backgrounds",
};

export interface BrandLogoControlProps {
  /** The image the mark goes on. Changes to the composited URL once applied. */
  imageUrl: string;
  customerId: string;
  idToken: string;
  /** Hands the composited image back so it replaces the original in the thread. */
  onApplied: (result: ApplyBrandLogoResponse) => void;
}

export const BrandLogoControl: React.FC<BrandLogoControlProps> = ({
  imageUrl,
  customerId,
  idToken,
  onApplied,
}) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ApplyBrandLogoResponse | null>(null);
  const [position, setPosition] = useState<LogoPosition>("top-right");
  /**
   * The variant the customer picked by hand, if they picked one.
   *
   * Held separately from `result.logo_variant` so it survives a position change.
   * Moving the mark re-measures the background, and without this a customer who
   * had deliberately overridden the suggestion would silently get the
   * measurement back the moment they nudged the logo to another corner — losing
   * the choice the whole feature exists to give them.
   */
  const [override, setOverride] = useState<LogoVariant | null>(null);

  // Always composite from the ORIGINAL image. Re-applying to an already-branded
  // result would stack a second mark on top of the first, and flipping the
  // variant would leave the old one showing through underneath.
  const run = async (variant?: LogoVariant, nextPosition?: LogoPosition) => {
    const usePosition = nextPosition ?? position;
    const useVariant = variant ?? override ?? undefined;
    setBusy(true);
    setError(null);
    try {
      const response = await applyBrandLogo(
        { customerId, imageUrl, position: usePosition, variant: useVariant },
        idToken
      );
      setResult(response);
      setPosition(usePosition);
      if (variant) setOverride(variant);
      onApplied(response);
    } catch (err: any) {
      debug.error("Brand logo placement failed:", err);
      setError(err?.message || "Could not place the logo.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          setOpen(true);
          void run();
        }}
        className="mt-2 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-[#2f4941] transition-colors hover:bg-[#2f4941]/10"
      >
        <span className="material-icons text-base">branding_watermark</span>
        Add logo
      </button>
    );
  }

  const otherVariant: LogoVariant | null =
    result && result.available_variants.length > 1
      ? result.logo_variant === "light"
        ? "dark"
        : "light"
      : null;

  return (
    <div className="mt-2 rounded-lg border border-[#2f4941]/20 bg-[#2f4941]/[0.03] p-3 text-xs">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-medium text-[#2f4941]">Brand logo</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[#2f4941]/60 transition-colors hover:text-[#2f4941]"
          aria-label="Close logo options"
        >
          <span className="material-icons text-base">close</span>
        </button>
      </div>

      <label className="mb-1 block text-[#2f4941]/70" htmlFor="brand-logo-position">
        Position
      </label>
      <select
        id="brand-logo-position"
        value={position}
        disabled={busy}
        onChange={(e) => void run(undefined, e.target.value as LogoPosition)}
        className="mb-2 w-full rounded border border-[#2f4941]/30 bg-white px-2 py-1 text-xs text-[#2f4941] disabled:opacity-60"
      >
        {LOGO_POSITIONS.map((value) => (
          <option key={value} value={value}>
            {POSITION_LABELS[value]}
          </option>
        ))}
      </select>

      {busy && <p className="text-[#2f4941]/70">Measuring the background…</p>}

      {!busy && error && <p className="text-red-600">{error}</p>}

      {!busy && !error && result && (
        <>
          <p className="text-[#2f4941]/70">
            Using the logo{" "}
            <strong>{VARIANT_LABELS[result.logo_variant].toLowerCase()}</strong>
            {result.auto_selected ? (
              <>
                {" — we measured "}
                <span
                  className="inline-block h-2.5 w-2.5 translate-y-[1px] rounded-sm border border-black/20 align-baseline"
                  style={{ backgroundColor: result.background_hex }}
                />{" "}
                {result.background_hex} under it.
              </>
            ) : (
              "."
            )}
          </p>

          {otherVariant && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void run(otherVariant)}
              className="mt-2 inline-flex items-center gap-1.5 rounded border border-[#2f4941]/30 px-2 py-1 font-medium text-[#2f4941] transition-colors hover:bg-[#2f4941]/10 disabled:opacity-60"
            >
              <span className="material-icons text-sm">swap_horiz</span>
              Use the one {VARIANT_LABELS[otherVariant].toLowerCase()}
            </button>
          )}

          {result.available_variants.length === 1 && (
            <p className="mt-2 text-[#2f4941]/60">
              This brand has one logo. Add a second under Brand Management to
              switch between light and dark backgrounds.
            </p>
          )}
        </>
      )}
    </div>
  );
};
